import * as React from 'react';
import { createFileRoute } from '@tanstack/react-router';
import { AdminLayout } from '@/components/admin/AdminLayout';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  Skeleton,
  Badge,
} from '@forgenetwork/ui';
import { useQuery } from '@tanstack/react-query';
import { api } from '@/lib/api';
import {
  Server,
  Database,
  HardDrive,
  Cpu,
  MemoryStick,
  AlertCircle,
  CheckCircle2,
} from 'lucide-react';

export const Route = createFileRoute('/admin/system')({
  component: AdminSystemPage,
});

interface SystemInfo {
  version: string;
  environment: string;
  uptime: number;
  nodeVersion: string;
  platform: string;
  arch: string;
  memory: {
    total: number;
    used: number;
    free: number;
    percentage: number;
  };
  cpu: {
    usage: number;
    cores: number;
  };
  disk: {
    total: number;
    used: number;
    free: number;
    percentage: number;
  };
}

interface DatabaseInfo {
  type: string;
  version: string;
  status: 'connected' | 'disconnected';
  connections: {
    active: number;
    idle: number;
    max: number;
  };
  latency: number;
}

function AdminSystemPage(): React.JSX.Element {
  const { data: systemInfo, isLoading: systemLoading } = useQuery({
    queryKey: ['admin', 'system', 'info'],
    queryFn: async () => {
      return api.get<SystemInfo>('/admin/system/info');
    },
  });

  const { data: databaseInfo, isLoading: dbLoading } = useQuery({
    queryKey: ['admin', 'system', 'database'],
    queryFn: async () => {
      return api.get<DatabaseInfo>('/admin/system/database');
    },
  });

  const isLoading = systemLoading || dbLoading;

  const formatBytes = (bytes: number): string => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + ' ' + sizes[i];
  };

  const formatUptime = (seconds: number): string => {
    const days = Math.floor(seconds / 86400);
    const hours = Math.floor((seconds % 86400) / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    return `${days}d ${hours}h ${minutes}m`;
  };

  return (
    <AdminLayout>
      <div className="space-y-6">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">System Configuration</h1>
          <p className="text-muted-foreground mt-2">
            View system information and configuration details
          </p>
        </div>

        {/* System Information */}
        <div className="grid gap-6 md:grid-cols-2">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Server className="h-5 w-5" />
                System Information
              </CardTitle>
              <CardDescription>Server and runtime details</CardDescription>
            </CardHeader>
            <CardContent>
              {isLoading ? (
                <div className="space-y-3">
                  <Skeleton className="h-4 w-full" />
                  <Skeleton className="h-4 w-full" />
                  <Skeleton className="h-4 w-full" />
                </div>
              ) : systemInfo ? (
                <div className="space-y-3 text-sm">
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Version</span>
                    <span className="font-medium">{systemInfo.version}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Environment</span>
                    <Badge variant="outline">{systemInfo.environment}</Badge>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Uptime</span>
                    <span className="font-medium">{formatUptime(systemInfo.uptime)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Node Version</span>
                    <span className="font-medium">{systemInfo.nodeVersion}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Platform</span>
                    <span className="font-medium">{systemInfo.platform}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Architecture</span>
                    <span className="font-medium">{systemInfo.arch}</span>
                  </div>
                </div>
              ) : (
                <div className="text-center py-4 text-muted-foreground">
                  Unable to load system information
                </div>
              )}
            </CardContent>
          </Card>

          {/* Database Information */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Database className="h-5 w-5" />
                Database Status
              </CardTitle>
              <CardDescription>Database connection and performance</CardDescription>
            </CardHeader>
            <CardContent>
              {isLoading ? (
                <div className="space-y-3">
                  <Skeleton className="h-4 w-full" />
                  <Skeleton className="h-4 w-full" />
                  <Skeleton className="h-4 w-full" />
                </div>
              ) : databaseInfo ? (
                <div className="space-y-3 text-sm">
                  <div className="flex justify-between items-center">
                    <span className="text-muted-foreground">Status</span>
                    {databaseInfo.status === 'connected' ? (
                      <Badge className="bg-green-500/20 text-green-700 dark:text-green-400">
                        <CheckCircle2 className="h-3 w-3 mr-1" />
                        Connected
                      </Badge>
                    ) : (
                      <Badge variant="destructive">
                        <AlertCircle className="h-3 w-3 mr-1" />
                        Disconnected
                      </Badge>
                    )}
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Type</span>
                    <span className="font-medium">{databaseInfo.type}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Version</span>
                    <span className="font-medium">{databaseInfo.version}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Active Connections</span>
                    <span className="font-medium">
                      {databaseInfo.connections.active} / {databaseInfo.connections.max}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Latency</span>
                    <span className="font-medium">{databaseInfo.latency}ms</span>
                  </div>
                </div>
              ) : (
                <div className="text-center py-4 text-muted-foreground">
                  Unable to load database information
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Resource Usage */}
        {systemInfo && (
          <div className="grid gap-6 md:grid-cols-3">
            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">Memory Usage</CardTitle>
                <MemoryStick className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">
                  {systemInfo.memory.percentage.toFixed(1)}%
                </div>
                <p className="text-xs text-muted-foreground mt-1">
                  {formatBytes(systemInfo.memory.used)} / {formatBytes(systemInfo.memory.total)}
                </p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">CPU Usage</CardTitle>
                <Cpu className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">
                  {systemInfo.cpu.usage.toFixed(1)}%
                </div>
                <p className="text-xs text-muted-foreground mt-1">
                  {systemInfo.cpu.cores} cores
                </p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">Disk Usage</CardTitle>
                <HardDrive className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">
                  {systemInfo.disk.percentage.toFixed(1)}%
                </div>
                <p className="text-xs text-muted-foreground mt-1">
                  {formatBytes(systemInfo.disk.used)} / {formatBytes(systemInfo.disk.total)}
                </p>
              </CardContent>
            </Card>
          </div>
        )}
      </div>
    </AdminLayout>
  );
}
